

// Probably need an ID gotten off the route, useParams mayhaps?

import { Box, FormControlLabel, Grid, Paper, styled, Switch, Typography, useTheme } from "@mui/material";
import { gridClasses, GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import { GRID_AGGREGATION_ROOT_FOOTER_ROW_ID, GridAggregationModel } from "@mui/x-data-grid-premium";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { ApiEnabledStatus, FormDefinitions, Permissions, StageSummary } from "../api/dtos";
import { DetailsContent, DetailsHeader, DetailsPanel, GridColumn, GridData, GridFillerRow, GridLabel, GridRow } from "../components/DetailsPanel";
import GridActions from "../components/GridActions";
import { StyledButton } from "../components/StyledButton";
import TitleBox from "../components/TitleBox";
import SetLinks from "../components/layout/SetLinks";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import { StyledLink } from "../components/utility/StyledLink";
import StageSummariesContext from "../contexts/StageSummariesContext";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import ProjectContext from "../contexts/data/ProjectContext";
import { DefaultProjectCreateLink, DefaultStageCreateLink } from "../contexts/layouts/LinkContext";
import useProjectDetailsById from "../hooks/data/useProjectDetailsById";
import useStageSummariesByProjectId from "../hooks/data/useStageSummariesByProjectId";
import useApiActivityStatus from "../hooks/data/useApiActivityStatusByStageId";
import usePostApiStatusUpdate from "../hooks/data/usePostApiStatusUpdate";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const useStageColumns = () => {
    const {data: permissions} = useContext(PermissionsContext);

    const metadata = useFormMetadata(FormDefinitions.Stage);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectId = Number(useParams().projectId);

    const stageColumns = useMemo(() => {
        const columns: GridColDef<StageSummary>[] =  [
            {
                field: "name",
                headerName: "Stage Name",
                minWidth: 100,
                flex: 1,
                renderCell: (params) => {
                    return (
                        <StyledLink 
                            to={`/projects/${projectId}/stages/${params.row.id}`}
                        >
                            {params.formattedValue}
                        </StyledLink>
                    )
                }
            },
            {
                field: "status",
                headerName: "Status",
                valueGetter: (_value, stage) => GetFieldLookupItem(lookups, 'Status', stage.status)?.value,
                width: 120
            },
            {
                field: "future",
                headerName: "Future Lots",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 100
            },
            {
                field: "unavailable",
                headerName: "Unavailable",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 130
            },
            {
                field: "comingSoon",
                headerName: "Coming Soon",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 100
            },
            {
                field: "onHold",
                headerName: "On Hold",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 90
            },
            {
                field: "available",
                headerName: "Available",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 110
            },
            {
                field: "salesAdviceIssued",
                headerName: "Sales Advice Issued",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 100
            },
            {
                field: "contractIssued",
                headerName: "Contract Issued",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 110
            },
            {
                field: "contractExchanged",
                headerName: "Contract Exchanged",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 120
            },
            {
                field: "contractSettled",
                headerName: "Contract Settled",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 110
            },
            {
                field: "totalLots",
                headerName: "Total Lots",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 100
            },
            {
                field: "unsoldLotValue",
                headerName: "Value of Unsold Lots",
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1,
                // valueFormatter: (_value, row) => {
                //     return row.unsoldLotValue ? Intl.NumberFormat("en-AU", {
                //         style: "currency",
                //         currency: "AUD"
                //     }).format(row.unsoldLotValue) : undefined;
                // },
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "exchangedLotValue",
                headerName: "Value of Exchanged Lots",
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "settledLotValue",
                headerName: "Value of Settled Lots",
                type: "number",
                headerAlign: "left",
                align: "left",
                flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
        ];

        const aggregationModel: GridAggregationModel = {
            future: 'sum',
            unavailable: 'sum',
            comingSoon: 'sum',
            onHold: 'sum',
            available: 'sum',
            salesAdviceIssued: 'sum',
            contractIssued: 'sum',
            contractExchanged: 'sum',
            contractSettled: 'sum',
            totalLots: 'sum',
            unsoldLotValue: 'sum',
            exchangedLotValue: 'sum',
            settledLotValue: 'sum'
        }

        if(permissions?.includes(Permissions.ProjectCanEditStage) || permissions?.includes(Permissions.ProjectCanDeleteStage)) {
            columns.push({
                field: "actions",
                headerName: '',
                width: 85,
                renderCell: (params) => {
                    return (
                        params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID ? 
                        null :
                        <GridActions
                            editPermission={Permissions.ProjectCanEditStage}
                            deletePermission={Permissions.ProjectCanDeleteStage}
                            editUrl={`/projects/${projectId}/stages/edit/${params.row.id}`}
                            deleteUrl={`/projects/${projectId}/stages/delete/${params.row.id}`}
                        />
                    )
                },
                
                filterable: false,
                sortable: false,
                disableExport: true,
                aggregable: false
            });
        }

        return {columns: columns, aggregationModel: aggregationModel};
    }, [lookups, permissions, projectId]);

    return stageColumns;
}

const columnGroupingModel: GridColumnGroupingModel = [
    {
        groupId: 'Unsold',
        description: '',
        headerClassName: 'column-group-header',
        children: [
            { field: 'unavailable'},
            { field: 'comingSoon'},
            { field: 'onHold'},
            { field: 'available'},
            { field: 'salesAdviceIssued'},
            { field: 'contractIssued'},
        ]
    },
    {
        groupId: "Sold",
        description: '',
        headerClassName: 'column-group-header',
        children: [
            { field: 'contractExchanged' },
            { field: 'contractSettled' }
        ]
    }
]

export const ProjectDetails: FC = () => {
    const projectMetadata = useFormMetadata(FormDefinitions.Project);

    const {data: permissions} = useContext(PermissionsContext);

    const theme = useTheme();
    
    const {data: projectLookups, loading: _lookupsLoading} = useLookups(projectMetadata);
    
    
    const params = useParams();
    
    const projectId = Number(params.projectId);

    const {data: projectApiStatus, refresh: refreshActivityStatus} = useApiActivityStatus(projectId, FormDefinitions.Project);
    
    const [showArchived, setShowArchivedStages] = useState(false);

    useEffect(() => {
        if(permissions?.includes(Permissions.ProjectCanSeeArchivedStages)) {
            setShowArchivedStages(true);
        }
    }, [permissions])
    
    const context = useStageSummariesByProjectId(projectId, showArchived);
    const {data: stageSummaries, loading: stagesLoading} = context;
    const {data: projectData, loading: _projectDataLoading} = useProjectDetailsById(projectId);
    
    const {columns: columns, aggregationModel: aggregationModel} = useStageColumns();

    const {data: project} = useContext(ProjectContext);
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name}`,
        fileName: `${project?.name}`,
        includeDateTime: true
    }), [project]);
    
    const updateApiStatus = usePostApiStatusUpdate();

    const toggleArchived = () => {
        setShowArchivedStages(showArchived => !showArchived);
    }

    const handleApiToggle = async (event: ChangeEvent<HTMLInputElement>) => {
        await updateApiStatus(projectId, FormDefinitions.Project, event.target.checked);
        refreshActivityStatus();
    }

    return (

        //Properties

        //Summary

        //StageList
        <StageSummariesContext.Provider value={context}>
            <Outlet />

            <SetLinks 
                createProjectLink={DefaultProjectCreateLink} 
                createStageLink={DefaultStageCreateLink} 
                lotLinkEnabled={false} 
            />
            <TitleBox>
                <Typography
                    variant="h2"
                >
                    {projectData?.project?.name}
                </Typography>
                {
                    permissions?.includes(Permissions.AdminAccess) &&
                    <Grid>
                        <FormControlLabel
                            label={
                                "Enable Api" + (
                                    projectApiStatus == ApiEnabledStatus.Active 
                                        ? ` - Id: ${projectId}`
                                        : ""
                                )
                            }
                            control={
                                <Switch
                                    checked={projectApiStatus == ApiEnabledStatus.Active}
                                    disabled={projectApiStatus == ApiEnabledStatus.Unused}
                                    onChange={handleApiToggle}
                                />
                            }
                        />
                    </Grid>
                }
                <Grid
                    container
                    gap={theme.spacing(2)}
                    justifyContent={"end"}
                >
                    {
                        permissions?.includes(Permissions.ProjectsCanEditProject) &&
                        <StyledButton 
                                variant="contained"
                                //@ts-expect-error
                                component={Link}
                                to={`/projects/${projectId}/edit`}
                                state={{
                                    back: true
                                }}
                        >
                            Edit Project
                        </StyledButton>
                    }
                    {
                        permissions?.includes(Permissions.ProjectCanEditCommissions) &&
                        <StyledButton 
                                variant="contained"
                                //@ts-expect-error
                                component={Link}
                                to={`/projects/${projectId}/project-commissions/edit`}
                                state={{
                                    back: true
                                }}
                        >
                            Edit Commission
                        </StyledButton>
                    }
                    {
                        permissions?.includes(Permissions.ProjectCanEditPrecincts) &&
                        <StyledButton 
                                variant="contained"
                                //@ts-expect-error
                                component={Link}
                                to={`/projects/${projectId}/project-precincts/edit`}
                                state={{
                                    back: true
                                }}
                        >
                            Edit Precincts
                        </StyledButton>
                    }
                    {
                        permissions?.includes(Permissions.ProjectCanAddStage) &&
                        <StyledButton 
                                variant="contained"
                                //@ts-expect-error
                                component={Link}
                                to={`/projects/${projectId}/stages/create`}
                                state={{
                                    back: true
                                }}
                        >
                            Add Stage
                        </StyledButton>
                    }
                </Grid>
            </TitleBox>
            
            <Grid
                container
                size={{
                    xs: 12
                }}
                spacing={{
                    xs: 2
                }}
                
                marginBottom={theme.spacing(2)}
            >
                <Grid
                    size={{
                        xs: 12,
                        lg: 6
                    }}
                >
                    <DetailsPanel>
                        <DetailsHeader>
                            Properties
                        </DetailsHeader>
                        <DetailsContent>
                            <GridColumn 
                                size={{
                                    xs: 12,
                                    md: 6
                                }}
                            >
                                <GridRow>
                                    <GridLabel>
                                        Project Type:
                                    </GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(projectLookups ,"Type", projectData?.project?.type)?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Project Status:
                                    </GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(projectLookups ,"Status", projectData?.project?.status)?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Share:
                                    </GridLabel>
                                    <GridData>
                                        {projectData?.project?.percentShare}%
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Default Back on Market (Days):
                                    </GridLabel>
                                    <GridData>
                                        {projectData?.project?.defaultBackOnMarket}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Margin Scheme:
                                    </GridLabel>
                                    <GridData>
                                        {projectData?.project?.marginScheme ? "Yes" : "No"}
                                    </GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                            <GridColumn 
                                size={{
                                    xs: 12,
                                    md: 6
                                }}
                            >
                                <GridRow>
                                    <GridLabel>
                                        Landowner:
                                    </GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(projectLookups ,"Landowner", projectData?.project?.landowner)?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Developer:
                                    </GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(projectLookups ,"Developer", projectData?.project?.developer)?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Developer Charge:
                                    </GridLabel>
                                    <GridData>
                                        {projectData?.project?.developerCharge}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        IA Commission:
                                    </GridLabel>
                                    <GridData>
                                        {
                                                projectData?.project?.iaCommission ? Intl.NumberFormat("en-AU", {
                                                    style: "currency",
                                                    currency: "AUD"
                                                }).format(projectData?.project?.iaCommission) : undefined
                                        }
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Landowner Per Lot Rate:
                                    </GridLabel>
                                    <GridData>
                                        {
                                                projectData?.project?.landownerPerLotRate ? Intl.NumberFormat("en-AU", {
                                                    style: "currency",
                                                    currency: "AUD"
                                                }).format(projectData?.project?.landownerPerLotRate) : undefined
                                        }
                                    </GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                        </DetailsContent>
                    </DetailsPanel>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        lg: 6
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch'
                    }}
                >
                    <DetailsPanel>
                        <DetailsHeader>
                            Summary
                        </DetailsHeader>
                        <DetailsContent>
                            <GridColumn 
                                size={{
                                    xs: 12,
                                    md: 6
                                }}                  
                            >
                                <GridRow>
                                    <GridLabel>
                                        Total Lots in Future Stages: 
                                    </GridLabel>
                                    <GridData>
                                        {projectData?.futureStageLots}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Total Lots in Current Stages: 
                                    </GridLabel>
                                    <GridData>
                                        {projectData?.currentStageLots}
                                    </GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                            <GridColumn 
                                size={{
                                    xs: 12,
                                    md: 6
                                }}                  
                            >
                                <GridRow>
                                    <GridLabel>
                                        Value of Future Stages:
                                    </GridLabel>
                                    <GridData>
                                        {
                                            projectData?.futureStageValue ? Intl.NumberFormat("en-AU", {
                                                style: "currency",
                                                currency: "AUD"
                                            }).format(projectData?.futureStageValue) : undefined
                                        }
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>
                                        Cost to Complete Future Stages: 
                                    </GridLabel>
                                    <GridData>
                                        {
                                            projectData?.futureStageCompletionCost ? Intl.NumberFormat("en-AU", {
                                                style: "currency",
                                                currency: "AUD"
                                            }).format(projectData?.futureStageCompletionCost) : undefined
                                        }
                                    </GridData>
                                </GridRow>
                                <GridRow>    
                                    <GridLabel>
                                        Value of Current Stages: 
                                    </GridLabel>
                                    <GridData>
                                        {
                                            projectData?.currentStageValue ? Intl.NumberFormat("en-AU", {
                                                style: "currency",
                                                currency: "AUD"
                                            }).format(projectData?.currentStageValue) : undefined
                                        }
                                    </GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                        </DetailsContent>
                    </DetailsPanel>
                </Grid>

            </Grid>
            {
                permissions?.includes(Permissions.ProjectCanSeeArchivedStages) &&
                <FormControlLabel
                    label="Show Archived Stages"
                    control={
                        <Switch 
                        checked={showArchived}
                            onChange={toggleArchived}
                        />
                    }
                />
            }
            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        startCollapsed={false} 
                        title={"Stages"} 
                        loading={stagesLoading} 
                        columns={columns}
                        rows={stageSummaries}
                        columnGroupingModel={columnGroupingModel}
                        aggregationModel={aggregationModel}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        pagination
                        {...customToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </StageSummariesContext.Provider>
    );
}